import React, { FC, useEffect, useRef, useState } from "react";
import "./MainNavbar.scss";
import { Container } from "react-bootstrap";
import logo from "../../assets/img/bevi-ajuda-blue.svg";
import { BvIcon } from "bevi-icon";
import { useAuth } from "../../providers/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../Spinner/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignRight, faBars } from "@fortawesome/free-solid-svg-icons";
import { useMediaQuery } from "react-responsive";
import useIsMobileApp from "../../hooks/useIsMobileApp";
import { UserRoleEnum } from "../../enums/User/UserRoleEnum";
import { toast } from "react-toastify";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import { ReactComponent as VectorDow } from '../../assets/icons/Vector 2.svg';
import { ReactComponent as Logout} from '../../assets/icons/logout-solid-600.svg'

const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getFirstLastName = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

interface MainNavbarProps {
  logoOnly?: boolean;
}

const MainNavbar: FC<MainNavbarProps> = ({ logoOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [isOpenMenuMobline, setIsOpenMenuMobline] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false);
  const { userLogged, logout, profileImageUrl } = useAuth();
  const isMobile = useMediaQuery({ maxWidth: 820 });
  const isPhone = useMediaQuery({ maxWidth: 576 });
  // Logado no mobile: fica no chat da IA
  const isChatOnly = useIsMobileApp() && !!userLogged;
  const navigate = useNavigate();
  const { statusTutorial } = useUsers();
  const { handleStatusTutorial } = useUsers();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleStatusTutorial();
  }, []);

  useEffect(() => {
    if (isPhone) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen, isPhone]);

  const handleLogin = async () => {
    setLoading(true);

    setTimeout(() => {
      navigate("/login");
      setLoading(false);
    }, 2000);
  };

  const handleLogout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const result = await logout().then();
    if (result?.success) {
      window.location.href = "/login-notice";
    } else {
      toast.error(`Erro ao sair: ${result?.error}`);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuClosing(true);
    setTimeout(() => {
      setIsUserMenuOpen(false);
      setIsMobileMenuClosing(false);
    }, 280);
  };

  const toggleNavBarMobile = () => {
    setIsOpenMenuMobline(!isOpenMenuMobline);
  };

  return (
    <>
      <div className="MainNavbar" data-testid="MainNavbar">
        <nav
          id="navbarBeviLearn"
          className="bg-white navbar navbar-main navbar-expand-lg px-0 mx-0 z-index-sticky fixed-top w-100"
        >
          <Container>
            <nav className="navbar navbar-expand-lg w-100 shadow-none px-0">
              <div className="container-fluid px-0">
                {isChatOnly ? (
                  <span className="navbar-brand d-flex align-items-center">
                    <img src={logo} alt="Logo" className="logo-image" />
                  </span>
                ) : (
                  <Link className="navbar-brand d-flex align-items-center" to="/">
                    <img src={logo} alt="Logo" className="logo-image" />
                  </Link>
                )}

                {isMobile && !logoOnly && !userLogged && (
                  <button
                    type="button"
                    className="nav-link p-0"
                    onClick={() => toggleNavBarMobile()}
                  >
                    {!isOpenMenuMobline ? (
                      <div className="sidenav-toggler-inner">
                        <FontAwesomeIcon icon={faBars} />
                      </div>
                    ) : (
                      <div className="sidenav-toggler-inner">
                        <FontAwesomeIcon icon={faAlignRight} />
                      </div>
                    )}
                  </button>
                )}

                {!logoOnly && !userLogged && (
                <div
                  className={`navbar-align collapse navbar-collapse ${isOpenMenuMobline ? "show" : ""
                    }`}
                  id="navbarNav"
                >
                  <div className="navbar-menu-content">
                    <div className="navbar-menu-links">
                      <a onClick={handleLogin} className="link-nav">
                        Entrar
                      </a>
                    </div>
                  </div>
                </div>
                )}

                {!logoOnly && userLogged && (
                <div className="navbar-right-group">
                  {!isPhone && (
                    <span className="navbar-user-summary">
                      {userLogged?.partner_code}
                      {userLogged?.partner_code && " - "}
                      {getFirstLastName(userLogged?.name)}
                    </span>
                  )}
                  {/* Tutorial obrigatório desativado  — sempre vai direto pro chat. */}
                  {/* to={statusTutorial === 0 ? "/chats/tutorial" : "/chats/reply"} */}
                  {!isChatOnly && (userLogged?.role == UserRoleEnum.ADMIN || userLogged?.access_profile?.id != 2) && (
                    <Link className="link-nav" to={"/chats/reply"}>
                      IA Beviajuda
                    </Link>
                  )}
                  <div className="user-menu-wrapper" ref={userMenuRef}>
                    <button
                      type="button"
                      className="user-menu-trigger"
                      onClick={() => !isChatOnly && setIsUserMenuOpen((prev) => !prev)}
                      style={isChatOnly ? { pointerEvents: "none", cursor: "default" } : undefined}
                      tabIndex={isChatOnly ? -1 : undefined}
                    >
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt="Avatar"
                          className="user-menu-avatar"
                        />
                      ) : (
                        <div className="user-menu-avatar user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                          <span className="user-menu-initials user-menu-initials-sm">{getInitials(userLogged?.name)}</span>
                        </div>
                      )}
                      {!isPhone && (
                        <VectorDow
                          className={`user-menu-chevron ${isUserMenuOpen ? "open" : ""}`}
                          width={6}
                          height={3}
                        />
                      )}
                    </button>

                    {isUserMenuOpen && !isPhone && !isChatOnly && (
                      <div className="user-menu-dropdown">
                        <div className="user-menu-header">
                          {profileImageUrl ? (
                            <img
                              src={profileImageUrl}
                              alt="Avatar"
                              className="user-menu-avatar-lg"
                            />
                          ) : (
                            <div className="user-menu-avatar-lg user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                              <span className="user-menu-initials user-menu-initials-lg">{getInitials(userLogged?.name)}</span>
                            </div>
                          )}
                          <h6 className="user-menu-greeting">
                            Olá, <span className="txt-username">{userLogged?.name?.split(" ")[0].toLowerCase() ?? ""}!</span>
                          </h6>
                          {userLogged?.partner_code && (
                            <span className="user-menu-partner-code">
                              {userLogged.partner_code}
                            </span>
                          )}
                        </div>

                        <ul className="user-menu-list">
                          {userLogged?.role === UserRoleEnum.ADMIN && !isChatOnly && (
                          <li>
                            <Link
                              to="/profile/account"
                              className="user-menu-item"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <span className="user-menu-item-left">
                                <BvIcon name="settings" variant="dark" height={16} width={16} />
                                Gerenciar conta
                              </span>
                             <BvIcon name="chevronRight" height={16} width={16}/>
                            </Link>
                          </li>
                          )}

                          {userLogged?.role === UserRoleEnum.ADMIN && !isChatOnly && (
                            <li>
                              <Link
                                to="/home"
                                className="user-menu-item"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <span className="user-menu-item-left">
                                  <BvIcon name="dashboardScreen" variant="dark" height={16} width={16} />
                                  Painel Adm.
                                </span>
                               <BvIcon name="chevronRight" height={16} width={16}/>
                              </Link>
                            </li>
                          )}

                          <li>
                            <a
                              className="user-menu-item"
                              onClick={(e) => { setIsUserMenuOpen(false); handleLogout(e); }}
                            >
                              <span className="user-menu-item-left">
                               <Logout className="" width={16} height={16} />
                                Sair
                              </span>
                             <BvIcon name="chevronRight" height={16} width={16}/>
                            </a>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {isUserMenuOpen && isPhone && userLogged && !isChatOnly && (
                  <div className={`user-menu-mobile-overlay ${isMobileMenuClosing ? "closing" : ""}`} onClick={closeMobileMenu}>
                    <div className={`user-menu-mobile-sheet ${isMobileMenuClosing ? "closing" : ""}`} onClick={(e) => e.stopPropagation()}>
                      <div className="user-menu-mobile-header">
                        {profileImageUrl ? (
                          <img
                            src={profileImageUrl}
                            alt="Avatar"
                            className="user-menu-avatar-lg"
                          />
                        ) : (
                          <div className="user-menu-avatar-lg user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                            <span className="user-menu-initials user-menu-initials-lg">{getInitials(userLogged?.name)}</span>
                          </div>
                        )}
                        <h6 className="user-menu-greeting">
                          Olá, <span className="txt-username">{userLogged?.name?.split(" ")[0].toLowerCase() ?? ""}!</span>
                        </h6>
                        {userLogged?.partner_code && (
                          <span className="user-menu-partner-code">
                            {userLogged.partner_code}
                          </span>
                        )}
                      </div>

                      <ul className="user-menu-list">
                        {userLogged?.role === UserRoleEnum.ADMIN && !isChatOnly && (
                        <li>
                          <Link
                            to="/profile/account"
                            className="user-menu-item"
                            onClick={closeMobileMenu}
                          >
                            <span className="user-menu-item-left">
                              <BvIcon name="settings" variant="dark" height={16} width={16} />
                              Gerenciar conta
                            </span>
                            <BvIcon name="chevronRight" height={16} width={16}/>
                          </Link>
                        </li>
                        )}

                        {userLogged?.role === UserRoleEnum.ADMIN && !isChatOnly && (
                          <li>
                            <Link
                              to="/home"
                              className="user-menu-item"
                              onClick={closeMobileMenu}
                            >
                              <span className="user-menu-item-left">
                                <BvIcon name="dashboardScreen" variant="dark" height={16} width={16} />
                                Painel Adm.
                              </span>
                              <BvIcon name="chevronRight" height={16} width={16}/>
                            </Link>
                          </li>
                        )}

                        <li>
                          <a
                            className="user-menu-item"
                            onClick={(e) => { closeMobileMenu(); handleLogout(e); }}
                          >
                            <span className="user-menu-item-left">
                              <Logout className="" width={16} height={16} />
                              Sair
                            </span>
                            <BvIcon name="chevronRight" height={16} width={16}/>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </Container>
        </nav>
      </div>
      {loading && <Spinner color="#25CBDB" />}
    </>
  );
};

export default MainNavbar;
